import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { DataService } from '../core/services/data.service';
import { AnalyticsService } from '../core/services/analytics.service';
import { EncryptionService } from '../core/services/encryption.service';
import { ApiCacheService } from '../core/services/api-cache.service';
import { EventEditLauncherService } from '../core/services/event-edit-launcher.service';
import { ToastService } from '../core/services/toast.service';
import { CreatedEvent, CreateEventFormComponent } from '../create-event-form/create-event-form.component';
import { openInviteShareDialog } from '../invite-share/open-invite-share';
import { INVITE_CLOSED_FOR_EDIT } from '../invite-share/invite-share.component';
import { environment } from '../../environments/environment';

/**
 * The in-app "Create Event" sheet — a bottom sheet on phones, a 480px dialog
 * on desktop.
 *
 * A frame around the shared CreateEventFormComponent, the same form the
 * new-user page (/auth/start) uses: this sheet supplies only its header, the
 * account's name for "Hosted by", and what happens after the event exists.
 * Everything the form does not ask for — location, RSVP, invite message,
 * albums, timezone — is editable afterwards in edit-event.
 *
 * After create it opens the invite screen exactly the way a past event does
 * (openInviteShareDialog). With `newInviteDesign` off it keeps the older
 * in-sheet share step instead.
 */
@Component({
  selector: 'app-create-event-sheet',
  templateUrl: './create-event-sheet.component.html',
  styleUrls: ['./create-event-sheet.component.scss'],
})
export class CreateEventSheetComponent implements OnInit {

  /** 'form' until the event exists; 'invite' is the legacy in-sheet share step. */
  step: 'form' | 'invite' = 'form';

  /** Read by home before the sheet closes, to decide whether to refresh its list. */
  createdEventId: any = null;
  created: CreatedEvent | null = null;
  isCreating = false;

  @ViewChild(CreateEventFormComponent) private eventForm?: CreateEventFormComponent;

  /** "Hosted by", pre-filled from the account. */
  readonly accountName: string;

  constructor(
    private dialogRef: MatDialogRef<CreateEventSheetComponent>,
    private dialog: MatDialog,
    private router: Router,
    private dataService: DataService,
    private analytics: AnalyticsService,
    private encryptionService: EncryptionService,
    private apiCache: ApiCacheService,
    private editLauncher: EventEditLauncherService,
    private toastService: ToastService,
  ) {
    this.accountName = this.readAccountName();
  }

  ngOnInit(): void {
    this.analytics.capture('create_event_started', { surface: 'sheet' });
  }

  get isPostCreate(): boolean {
    return this.step === 'invite';
  }

  /** Host name from the account, falling back to the Firebase display name. */
  private readAccountName(): string {
    if (this.dataService.userName) return this.dataService.userName;
    try {
      const decrypted = this.encryptionService.decryptData(localStorage.getItem('user') || '');
      const user = JSON.parse(decrypted || '{}');
      return user?.displayName || '';
    } catch {
      return '';
    }
  }

  /** Before creation this cancels; afterwards the event exists, so it lands on it. */
  close(): void {
    if (this.isPostCreate) {
      this.finish();
      return;
    }
    if (this.isCreating) return;
    // Dismissed before the event was created — the drop-off point in the funnel.
    // Which answers were filled when they left — the single-screen form has no
    // steps to report, so this is how far they got.
    this.analytics.capture('create_event_abandoned', {
      surface: 'sheet',
      ...(this.eventForm?.answersSummary() || {}),
    });
    this.dialogRef.close();
  }

  onCreated(event: CreatedEvent): void {
    this.created = event;
    this.createdEventId = event.eventId;

    if (environment.features.newInviteDesign) {
      this.handOffToInviteDialog(event);
      return;
    }
    this.step = 'invite';
  }

  /**
   * The share step, opened exactly the way a past event (and the new-user page)
   * opens it: the 880px two-column dialog on desktop, the bottom sheet on a
   * phone. The sheet's job is done once the event exists; closing the invite
   * lands on the new event, as finishing always has.
   */
  private handOffToInviteDialog(event: CreatedEvent): void {
    openInviteShareDialog(this.dialog, {
      eventId: event.eventId,
      eventName: event.eventName,
      eventStartTime: event.start,
      coverImageUrl: event.coverImage || '',
      hostName: event.hostedBy,
      surface: 'sheet',
      mode: 'created',
      eventHex: event.viewerHex,
    }).afterClosed().subscribe(result => {
      // "Edit details" is already on its way to the editor; landing on the
      // event here would race it.
      if (result === INVITE_CLOSED_FOR_EDIT) return;
      this.landOnEvent();
    });
    this.dialogRef.close(true);
  }

  /**
   * The form's "Edit it instead": they were about to make an event they made a
   * few minutes ago. Close without creating and open that one's details.
   */
  async editExisting(eventHex: string): Promise<void> {
    this.dialogRef.close();
    const opened = await this.editLauncher.openDetails(eventHex);
    if (!opened) {
      this.toastService.showToast("Couldn't open that event just now. Find it in your events to edit it.");
      this.router.navigate(['/home']);
    }
  }

  /** Closes the sheet and lands on the new event. */
  finish(): void {
    this.dialogRef.close(true);
    this.landOnEvent();
  }

  private landOnEvent(): void {
    // Invalidate here, synchronously, rather than relying on the dialog's
    // afterClosed() callback on the home side — that callback races the
    // navigate() below and can fire after (or never, if the navigation tears
    // the dialog down first), silently leaving home's event list stale.
    this.apiCache.clearCache('pwa_get_home_page_data');
    // The category count is cached the same way and gates home's paging, so a
    // stale one can stop the refreshed list from ever being requested.
    this.apiCache.clearCache('pwa_get_total_categories');
    this.router.navigate(['/event-detail/true']);
  }

  /**
   * The legacy share step's "See all templates" leaves the sheet for the
   * routed template gallery — same close-then-navigate shape as `finish()`.
   */
  browseInviteTemplates(): void {
    this.dialogRef.close();
    this.router.navigate(['/invitation']);
  }
}
